// Training Zone Calculation Utilities

/**
 * Calculate heart rate zones based on maximum HR
 * 
 * @param {number} maxHR - Maximum heart rate in bpm
 * @returns {Object} HR zones object
 */
export function calculateHRZonesFromMax(maxHR) {
  if (!maxHR || maxHR <= 0) {
    return null;
  }
  
  return {
    zone1: { min: Math.round(maxHR * 0.50), max: Math.round(maxHR * 0.60) },
    zone2: { min: Math.round(maxHR * 0.60), max: Math.round(maxHR * 0.70) },
    zone3: { min: Math.round(maxHR * 0.70), max: Math.round(maxHR * 0.80) },
    zone4: { min: Math.round(maxHR * 0.80), max: Math.round(maxHR * 0.90) },
    zone5: { min: Math.round(maxHR * 0.90), max: maxHR },
    maxHR,
    method: 'max'
  };
}

/**
 * Calculate heart rate zones based on lactate threshold HR
 * 
 * @param {number} lthr - Lactate threshold heart rate in bpm
 * @returns {Object} HR zones object
 */
export function calculateHRZonesFromLTHR(lthr) {
  if (!lthr || lthr <= 0) {
    return null;
  }
  
  return {
    zone1: { min: Math.round(lthr * 0.65), max: Math.round(lthr * 0.75) },
    zone2: { min: Math.round(lthr * 0.75), max: Math.round(lthr * 0.85) },
    zone3: { min: Math.round(lthr * 0.85), max: Math.round(lthr * 0.95) },
    zone4: { min: Math.round(lthr * 0.95), max: Math.round(lthr * 1.05) },
    zone5: { min: Math.round(lthr * 1.05), max: Math.round(lthr * 1.20) },
    lactateThresholdHR: lthr,
    method: 'lthr'
  };
}

/**
 * Calculate power zones based on FTP (Functional Threshold Power)
 * 
 * @param {number} ftp - Functional Threshold Power in watts
 * @returns {Object} Power zones object
 */
export function calculatePowerZonesFromFTP(ftp) {
  if (!ftp || ftp <= 0) {
    return null;
  }
  
  return {
    zone1: { min: 0, max: Math.round(ftp * 0.55) },
    zone2: { min: Math.round(ftp * 0.55), max: Math.round(ftp * 0.75) },
    zone3: { min: Math.round(ftp * 0.75), max: Math.round(ftp * 0.90) },
    zone4: { min: Math.round(ftp * 0.90), max: Math.round(ftp * 1.05) },
    zone5: { min: Math.round(ftp * 1.05), max: Math.round(ftp * 1.20) },
    ftp,
    method: 'ftp'
  };
}

/**
 * Calculate pace zones based on threshold pace
 * 
 * @param {number} thresholdPace - Threshold pace in seconds per km
 * @returns {Object} Pace zones object
 */
export function calculatePaceZonesFromThreshold(thresholdPace) {
  if (!thresholdPace || thresholdPace <= 0) {
    return null;
  }
  
  // Slower pace = higher seconds per km
  return {
    zone1: { min: Math.round(thresholdPace * 1.40), max: Math.round(thresholdPace * 1.25) },
    zone2: { min: Math.round(thresholdPace * 1.25), max: Math.round(thresholdPace * 1.15) },
    zone3: { min: Math.round(thresholdPace * 1.15), max: Math.round(thresholdPace * 1.05) },
    zone4: { min: Math.round(thresholdPace * 1.05), max: thresholdPace },
    zone5: { min: Math.round(thresholdPace * 0.95), max: Math.round(thresholdPace * 0.85) },
    thresholdPace,
    method: 'lactate'
  };
}

/**
 * Estimate max HR from age
 * Standard formula: 220 - age
 * 
 * @param {number} age - Age in years
 * @returns {number} Estimated max HR
 */
export function estimateMaxHRFromAge(age) {
  if (!age || age <= 0 || age > 120) {
    return null;
  }
  return Math.round(220 - age);
}

/**
 * Estimate FTP from max power
 * Typically FTP is 75-85% of max power
 * 
 * @param {number} maxPower - Maximum power in watts
 * @param {number} percentage - Percentage of max (default 0.75)
 * @returns {number} Estimated FTP
 */
export function estimateFTPFromMaxPower(maxPower, percentage = 0.75) {
  if (!maxPower || maxPower <= 0) {
    return null;
  }
  return Math.round(maxPower * percentage);
}

/**
 * Convert pace from min/km to seconds/km
 * 
 * @param {string|number} pace - Pace as "MM:SS" or minutes as number
 * @returns {number} Pace in seconds per km
 */
export function paceToSeconds(pace) {
  if (typeof pace === 'number') {
    return Math.round(pace * 60);
  }
  if (typeof pace === 'string' && pace.includes(':')) {
    const [minutes, seconds] = pace.split(':').map(Number);
    return minutes * 60 + seconds;
  }
  return null;
}

/**
 * Convert pace from seconds/km to MM:SS format
 * 
 * @param {number} seconds - Pace in seconds per km
 * @returns {string} Pace as "MM:SS"
 */
export function secondsToPace(seconds) {
  if (!seconds || seconds <= 0) {
    return '00:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}
